import { useAuth, useUser } from "@clerk/react"
import { useEffect } from "react"
import { useStoredState } from "../hooks/useStoredState"
import {
  EMPTY_PLAYER_PROFILE_STORE,
  PLAYER_PROFILES_STORAGE_KEY,
  PLAYER_PROFILES_STORAGE_VERSION,
  PlayerProfileStoreStateSchema,
} from "../profile/storage"
import { usePublishPublicProfileMutation } from "../social/query"
import { buildPublicPlayerProfile } from "./publicProfile"
import { getProfileByEmail, normalizeEmail } from "./validation"

const PRESENCE_HEARTBEAT_INTERVAL_MS = 2 * 60_000

export function ProfilePresenceHeartbeat() {
  const { userId } = useAuth()
  const { user } = useUser()
  const publishPublicProfileMutation = usePublishPublicProfileMutation()
  const [profiles] = useStoredState(
    PLAYER_PROFILES_STORAGE_KEY,
    PlayerProfileStoreStateSchema,
    {
      defaultValue: EMPTY_PLAYER_PROFILE_STORE,
      version: PLAYER_PROFILES_STORAGE_VERSION,
    },
  )

  const email = user?.primaryEmailAddress?.emailAddress?.trim() ?? ""
  const profile = email
    ? getProfileByEmail(profiles, normalizeEmail(email))
    : undefined

  useEffect(() => {
    if (!profile || !userId) return
    if (!profile.friendCode) return

    const publishPresence = () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return

      void publishPublicProfileMutation
        .mutateAsync(buildPublicPlayerProfile(profile, userId, user?.imageUrl))
        .catch(() => {
          // Presence heartbeats should not interrupt the rest of the app.
        })
    }

    const interval = window.setInterval(
      publishPresence,
      PRESENCE_HEARTBEAT_INTERVAL_MS,
    )
    const handleFocus = () => publishPresence()
    const handleOnline = () => publishPresence()
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        publishPresence()
      }
    }

    window.addEventListener("focus", handleFocus)
    window.addEventListener("online", handleOnline)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener("focus", handleFocus)
      window.removeEventListener("online", handleOnline)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [profile, publishPublicProfileMutation, user?.imageUrl, userId])

  return null
}
